'use client';

import React from 'react';
import clsx from 'clsx';
import { useTranslations } from '@/lib';
import type { StepsIndicatorProps } from './types';
import {
  alignStyles,
  barBase,
  barsWrapperBase,
  disabledClass,
  labelBase,
  variantStyles,
  wrapperBase,
} from './consts';

export const StepsIndicator = ({
  totalSteps,
  currentStep,
  type = 'step',
  variant = 'dark',
  disabled = false,
  showLabel = true,
  align = 'left',
  renderLabel,
  ariaLabel = 'Schritte',
}: StepsIndicatorProps) => {
  const { t } = useTranslations();

  const total = Math.max(1, totalSteps);
  const n = Number.isFinite(currentStep) ? currentStep : 1;
  const active = Math.min(total, Math.max(1, n));

  const labelNode = renderLabel?.({ current: active, total }) ?? (
    <>
      {t('stepindicator.step')} <span className="tabular-nums">{active}</span>{' '}
      {t('stepindicator.of')} <span className="tabular-nums">{total}</span>
    </>
  );

  return (
    <div
      data-component-id="CO33"
      className={clsx(
        wrapperBase,
        variantStyles[variant].text,
        alignStyles[align],
        disabled && disabledClass,
      )}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={active}
    >
      {showLabel && <div className={labelBase}>{labelNode}</div>}

      <div className={clsx(barsWrapperBase, alignStyles[align])} aria-hidden>
        {Array.from({ length: total }).map((_, i) => {
          const step = i + 1;
          const isActive =
            type !== 'progress' ? step === active : step <= active;

          return (
            <span
              key={step}
              className="flex min-w-0 flex-1 lg:w-[100px] lg:flex-none"
            >
              <span
                className={clsx(
                  barBase,
                  isActive
                    ? variantStyles[variant].barActive
                    : variantStyles[variant].barInactive,
                )}
              />
            </span>
          );
        })}
      </div>
    </div>
  );
};
